import React, { useEffect, useState } from "react";
import { Modal, Button, Input, Switch, Spin, Typography, notification } from "antd";
import { CopyOutlined, GlobalOutlined, LockOutlined } from "@ant-design/icons";

const { Text } = Typography;

const errorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (typeof detail?.message === "string") return detail.message;
  return fallback;
};

const copyWithTextarea = (text) => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  if (!copied) throw new Error("Copy failed");
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    copyWithTextarea(text);
  }
};

const ShareFolderModal = ({
  visible,
  onClose,
  folderName,
  loadShare,
  enableShare,
  disableShare,
}) => {
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible) {
      setShare(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    loadShare()
      .then((data) => {
        if (!cancelled) setShare(data);
      })
      .catch((error) => {
        if (cancelled) return;
        notification.error({
          message: "Failed to load sharing settings",
          description: errorMessage(error, "Please try again."),
        });
        onClose();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleCopy = async (url) => {
    try {
      await copyToClipboard(url);
      notification.success({ message: "Link copied" });
    } catch (error) {
      notification.error({
        message: "Could not copy the link",
        description: "Select the link and copy it manually.",
      });
    }
  };

  const handleToggle = async (checked) => {
    setSaving(true);
    try {
      const data = checked ? await enableShare() : await disableShare();
      setShare(data);
      notification.success({
        message: checked ? "Public link created" : "Public link turned off",
      });
    } catch (error) {
      notification.error({
        message: "Failed to update sharing",
        description: errorMessage(error, "Please try again."),
      });
    } finally {
      setSaving(false);
    }
  };

  const isPublic = !!share?.enabled;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={`Share "${folderName || "folder"}"`}
      footer={[
        isPublic && (
          <Button
            key="copy"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(share.share_url)}
          >
            Copy link
          </Button>
        ),
        <Button key="done" type="primary" onClick={onClose}>
          Done
        </Button>,
      ]}
      destroyOnClose
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center rounded-full w-9 h-9 flex-shrink-0 ${
                isPublic ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
              }`}
            >
              {isPublic ? <GlobalOutlined /> : <LockOutlined />}
            </div>
            <div className="flex-1">
              <Text strong>Public link</Text>
              <div className="text-gray-500 text-sm">
                {isPublic
                  ? "On: anyone with the link can view and download the files in this folder and its subfolders, without logging in."
                  : "Off: only project members can open this folder. Turn on to create a link you can share with anyone."}
              </div>
            </div>
            <Switch checked={isPublic} onChange={handleToggle} loading={saving} />
          </div>
          {isPublic && (
            <Input
              readOnly
              value={share.share_url}
              onFocus={(e) => e.target.select()}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default ShareFolderModal;
